import React from 'react';
import { ValuationSummary, SubletExpense, LaborRecord } from '../../types/dossier';
import { BOMItem, ComponentCondition } from '../../types/bom';
import { formatCurrency, formatPercent } from '../../utils/printFormatter';
import { ConditionBadge } from '../common/ConditionBadge';
import { calculateComponentVariance } from '../../utils/budgetCalculations';
import {
  Wrench,
  Package,
  Layers,
  ShieldCheck,
  Building,
} from 'lucide-react';

export interface DossierSummaryTableProps {
  valuation: ValuationSummary;
  bomItems: BOMItem[];
  subletExpenses: SubletExpense[];
  laborRecords: LaborRecord[];
  className?: string;
  isPrintMode?: boolean;
}

export const DossierSummaryTable: React.FC<DossierSummaryTableProps> = ({
  valuation,
  bomItems,
  subletExpenses,
  laborRecords,
  className = '',
  isPrintMode = false,
}) => {
  // Aggregate BOM items by the 4 mandatory conditions
  const conditions: ComponentCondition[] = [
    'OEM NOS',
    'Reproduction',
    'Salvage',
    'Remanufactured',
  ];

  const conditionBreakdown = conditions.map((condition) => {
    const matchingItems = bomItems.filter(
      (item) =>
        item.condition === condition &&
        (item.status === 'Sourced' || item.status === 'Installed')
    );
    const count = matchingItems.length;
    const totalSpent = matchingItems.reduce((acc, item) => {
      const variance = calculateComponentVariance(item);
      return acc + variance.netActualCost;
    }, 0);

    const pctOfParts =
      valuation.totalBOMPartsCost > 0
        ? (totalSpent / valuation.totalBOMPartsCost) * 100
        : 0;

    return {
      condition,
      count,
      totalSpent,
      pctOfParts,
    };
  });

  const totalPurchasedBOMCount = bomItems.filter(
    (i) => i.status === 'Sourced' || i.status === 'Installed'
  ).length;

  const totalLaborHours = laborRecords.reduce(
    (sum, record) => sum + (record.hours || 0),
    0
  );

  const averageLaborRate =
    totalLaborHours > 0
      ? valuation.totalInHouseLaborValuation / totalLaborHours
      : 85;

  return (
    <div className={`space-y-6 ${isPrintMode ? 'dossier-print-mode' : ''} ${className}`}>
      {/* 1. Primary Capital Investment Rollup Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 avoid-break">
        {/* Acquisition */}
        <div className="bg-shop-dark border border-shop-border p-3.5 rounded shadow-machined">
          <div className="flex items-center justify-between text-shop-muted mb-1">
            <span className="text-[11px] font-mono uppercase tracking-wider">
              1. ACQUISITION COST
            </span>
            <Building className="w-3.5 h-3.5 text-shop-muted" />
          </div>
          <div className="text-xl font-bold font-mono text-shop-highlight">
            {formatCurrency(valuation.acquisitionCost)}
          </div>
          <p className="text-[10px] text-shop-muted mt-1">
            Initial project vehicle purchase basis
          </p>
        </div>

        {/* BOM Parts */}
        <div className="bg-shop-dark border border-shop-border p-3.5 rounded shadow-machined">
          <div className="flex items-center justify-between text-shop-muted mb-1">
            <span className="text-[11px] font-mono uppercase tracking-wider">
              2. BOM REPLACEMENT PARTS
            </span>
            <Package className="w-3.5 h-3.5 text-safety-blue" />
          </div>
          <div className="text-xl font-bold font-mono text-shop-highlight">
            {formatCurrency(valuation.totalBOMPartsCost)}
          </div>
          <p className="text-[10px] text-shop-muted mt-1">
            {totalPurchasedBOMCount} Sourced &amp; Installed items
          </p>
        </div>

        {/* Sublet Services */}
        <div className="bg-shop-dark border border-shop-border p-3.5 rounded shadow-machined">
          <div className="flex items-center justify-between text-shop-muted mb-1">
            <span className="text-[11px] font-mono uppercase tracking-wider">
              3. SPECIALIST SUBLET
            </span>
            <Wrench className="w-3.5 h-3.5 text-safety-amber" />
          </div>
          <div className="text-xl font-bold font-mono text-shop-highlight">
            {formatCurrency(valuation.totalSubletLaborCost)}
          </div>
          <p className="text-[10px] text-shop-muted mt-1">
            {subletExpenses.length} Machining &amp; finish invoices
          </p>
        </div>

        {/* Restoration Labor */}
        <div className="bg-shop-dark border border-shop-border p-3.5 rounded shadow-machined">
          <div className="flex items-center justify-between text-shop-muted mb-1">
            <span className="text-[11px] font-mono uppercase tracking-wider">
              4. CERTIFIED LABOR
            </span>
            <Layers className="w-3.5 h-3.5 text-safety-green" />
          </div>
          <div className="text-xl font-bold font-mono text-shop-highlight">
            {formatCurrency(valuation.totalInHouseLaborValuation)}
          </div>
          <p className="text-[10px] text-shop-muted mt-1">
            {totalLaborHours} documented hrs @ ${Math.round(averageLaborRate)}/hr
          </p>
        </div>
      </div>

      {/* 2. Parts Investment Ledger by Component Condition */}
      <div className="bg-shop-dark border border-shop-border rounded overflow-hidden shadow-machined avoid-break">
        <div className="px-4 py-2.5 bg-shop-darkest border-b border-shop-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-safety-blue" />
            <h4 className="text-xs font-bold font-mono uppercase tracking-wider text-shop-highlight">
              BOM Component Investment Itemized by Provenance Condition
            </h4>
          </div>
          <span className="text-[11px] font-mono text-shop-muted">
            Total Sourced/Installed: {formatCurrency(valuation.totalBOMPartsCost)}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-shop-panel text-[11px] font-mono text-shop-muted uppercase border-b border-shop-border">
                <th className="py-2.5 px-4">Provenance Condition</th>
                <th className="py-2.5 px-4">Classification Details</th>
                <th className="py-2.5 px-4 text-center">Item Count</th>
                <th className="py-2.5 px-4 text-right">Capital Outlay</th>
                <th className="py-2.5 px-4 text-right">Parts %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-shop-border text-xs">
              {conditionBreakdown.map((row) => (
                <tr key={row.condition} className="hover:bg-shop-well/50 transition-colors">
                  <td className="py-2.5 px-4 font-mono font-bold">
                    <ConditionBadge condition={row.condition} size="sm" />
                  </td>
                  <td className="py-2.5 px-4 text-shop-muted">
                    {row.condition === 'OEM NOS' &&
                      'Original factory-era stock, unused, pristine provenance'}
                    {row.condition === 'Reproduction' &&
                      'Modern aftermarket replication manufactured to factory OEM specs'}
                    {row.condition === 'Salvage' &&
                      'Factory original core harvested from period donor vehicle'}
                    {row.condition === 'Remanufactured' &&
                      'Overhauled original assembly rebuilt with fresh wear components'}
                  </td>
                  <td className="py-2.5 px-4 text-center font-mono font-bold text-shop-light">
                    {row.count}
                  </td>
                  <td className="py-2.5 px-4 text-right font-mono font-bold text-shop-highlight">
                    {formatCurrency(row.totalSpent, { showCents: true })}
                  </td>
                  <td className="py-2.5 px-4 text-right font-mono text-shop-muted">
                    {formatPercent(row.pctOfParts)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-shop-darkest text-xs font-mono font-bold border-t-2 border-shop-border">
                <td colSpan={2} className="py-2.5 px-4 text-shop-highlight uppercase">
                  Total Documented BOM Parts
                </td>
                <td className="py-2.5 px-4 text-center text-shop-highlight">
                  {totalPurchasedBOMCount}
                </td>
                <td className="py-2.5 px-4 text-right text-safety-blue">
                  {formatCurrency(valuation.totalBOMPartsCost, { showCents: true })}
                </td>
                <td className="py-2.5 px-4 text-right text-shop-highlight">100.0%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* 3. Agreed-Value Policy Valuation Matrix */}
      <div className="bg-shop-dark border-2 border-shop-border rounded overflow-hidden shadow-tactile avoid-break">
        <div className="px-4 py-3 bg-shop-darkest border-b border-shop-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-safety-amber" />
            <h4 className="text-sm font-bold font-mono uppercase tracking-wider text-shop-highlight">
              Collector Vehicle Agreed-Value Valuation Calculation
            </h4>
          </div>
          <span className="text-xs font-mono font-bold text-safety-amber bg-safety-amber/10 px-2 py-0.5 rounded border border-safety-amber">
            HAGERTY / GRUNDY UNDERWRITING FORMULA
          </span>
        </div>

        <div className="p-4 space-y-4">
          <div className="space-y-2 text-xs font-mono">
            <div className="flex justify-between py-1.5 border-b border-shop-border/70 text-shop-light">
              <span>A. Initial Vehicle Acquisition Investment:</span>
              <span className="font-bold text-shop-highlight">
                {formatCurrency(valuation.acquisitionCost, { showCents: true })}
              </span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-shop-border/70 text-shop-light">
              <span>B. Bill of Materials (BOM) Replacement Parts Actuals:</span>
              <span className="font-bold text-shop-highlight">
                {formatCurrency(valuation.totalBOMPartsCost, { showCents: true })}
              </span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-shop-border/70 text-shop-light">
              <span>C. Specialist Sublet Machining &amp; Finishing Invoices:</span>
              <span className="font-bold text-shop-highlight">
                {formatCurrency(valuation.totalSubletLaborCost, { showCents: true })}
              </span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-shop-border/70 text-shop-light">
              <span>
                D. Certified In-House Restoration Labor ({totalLaborHours} hrs @ $
                {Math.round(averageLaborRate)}/hr):
              </span>
              <span className="font-bold text-shop-highlight">
                {formatCurrency(valuation.totalInHouseLaborValuation, { showCents: true })}
              </span>
            </div>

            <div className="flex justify-between py-2 border-b-2 border-shop-border text-sm font-bold text-shop-highlight bg-shop-panel/50 px-2 rounded">
              <span>TOTAL CAPITAL INVESTED BASIS (A + B + C + D):</span>
              <span className="text-blueprint-cyan">
                {formatCurrency(valuation.totalInvestedCapital, { showCents: true })}
              </span>
            </div>

            <div className="flex justify-between py-1.5 border-b border-shop-border/70 text-shop-light">
              <span>
                E. Collector Vehicle Replacement &amp; Appreciation Buffer (+
                {valuation.appreciationBufferPct}%):
              </span>
              <span className="font-bold text-safety-green">
                +
                {formatCurrency(
                  valuation.suggestedAgreedValue - valuation.totalInvestedCapital,
                  { showCents: true }
                )}
              </span>
            </div>

            <div className="flex justify-between items-center pt-2 pb-1 text-base font-bold bg-shop-darkest px-3 py-2.5 rounded border border-safety-amber">
              <span className="text-safety-amber uppercase tracking-wider">
                RECOMMENDED AGREED-VALUE POLICY BOUND:
              </span>
              <span className="text-xl font-mono text-safety-amber">
                {formatCurrency(valuation.suggestedAgreedValue)}
              </span>
            </div>
          </div>

          <p className="text-[11px] text-shop-muted leading-relaxed italic border-t border-shop-border pt-2">
            * Note for Underwriters: The Suggested Agreed-Value reflects 100% actual receipted
            capital outlays plus verified restoration hours and an actuarial replacement
            scarcity buffer. Does not reflect speculative retail markup.
          </p>
        </div>
      </div>
    </div>
  );
};
